/**
 * stories.js — the one place every highlights story is described.
 *
 * Previously the story list lived only in mobile/HighlightsScreen.js while the
 * routes lived in App.js, so adding a reel meant editing both and nothing knew
 * which stories a logged-out visitor may see. This module owns all of it.
 *
 * ── THE ACCESS TOGGLE ────────────────────────────────────────────────────────
 * Each story carries `access`:
 *
 *   ACCESS.PUBLIC — anyone can open the link, logged in or not. No auth request
 *                   is made and nothing renders differently for a signed-in
 *                   user, so a public story is exactly as fast as it was before
 *                   the gate existed. The reel's X exits to the login page for a
 *                   logged-out visitor and into the app for a signed-in one.
 *   ACCESS.GATED  — a logged-out visitor is sent to the login screen first and
 *                   returned to the story once they sign in (see StoryGate and
 *                   utils/storyEntry).
 *
 * FLIPPING A STORY is a one-word edit to its `access` below — no other file
 * needs to change.
 *
 * SCOPE OF THE GATE (deliberate, and worth knowing): this is a client-side gate.
 * It controls what the app routes people to; it is not a security boundary. The
 * survey endpoints these reels read are currently unauthenticated, so a gated
 * story's underlying numbers remain fetchable by anyone who goes looking.
 * Locking the backend down is tracked separately.
 */
export const ACCESS = {
  PUBLIC: "public",
  GATED: "gated",
};

export const STORY_PREFIX = "/highlights/";

export const STORIES = [
  {
    slug: "intro",
    route: "/highlights/intro",
    access: ACCESS.GATED,
    title: "Intro to District Profiles",
    description:
      "A guided tour of what District Profiles can do — local estimates, demographic filters, and district-level targeting.",
  },
  {
    slug: "prediction-markets",
    route: "/highlights/prediction-markets",
    access: ACCESS.PUBLIC,
    title: "Prediction Markets",
    description:
      "How Americans want prediction markets regulated, tracked across waves, plus who they think should regulate them and where the frame is moving fastest.",
  },
  {
    slug: "ai-boom",
    route: "/highlights/ai-boom",
    access: ACCESS.GATED,
    title: "AI & Data Centers",
    description:
      "How the AI build-out is landing with the public: three waves of sentiment, who is actually using AI, and what happens when a data center or an AI-made ad turns up close to home.",
  },
  {
    slug: "nji-creative",
    route: "/highlights/nji-creative",
    access: ACCESS.PUBLIC,
    title: "The human premium",
    description:
      "A national look at what makes advertising feel trustworthy, authentic, and recognisably human as AI-generated content becomes more common.",
  },
  {
    slug: "selfdriving",
    route: "/highlights/selfdriving",
    access: ACCESS.PUBLIC,
    title: "Who's ready to ride?",
    description:
      "Tracking comfort with self-driving vehicles: where it's growing, who it belongs to, and how it maps onto daily AI use.",
  },
];

/**
 * Is this a story route at all? Also the open-redirect guard used wherever a
 * stored path is read back before being handed to navigate().
 */
export function isStoryPath(path) {
  return typeof path === "string" && path.startsWith(STORY_PREFIX);
}

/** Look a story up by slug or by route/pathname. Returns undefined if unknown. */
export function getStory(slugOrPath) {
  if (typeof slugOrPath !== "string") return undefined;
  return STORIES.find((s) => s.slug === slugOrPath || s.route === slugOrPath);
}

/**
 * True only for a story explicitly marked PUBLIC. An unrecognised path is NOT
 * public — a story added to App.js but forgotten here fails to the safe side.
 */
export function isPublicStory(slugOrPath) {
  return getStory(slugOrPath)?.access === ACCESS.PUBLIC;
}
